"use client";

import { router } from "@inertiajs/react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TRUNCATE_THRESHOLD } from "@/lib/constants";
import { organizationQuoteItemPath } from "@/routes";
import type { QuoteItem, RequestItem } from "@/types";

interface QuoteItemMatchSelectorProps {
  quoteItem: QuoteItem & {
    request_item?: {
      id: number;
      description: string;
      item_number: number;
    };
  };
  availableRequestItems: RequestItem[];
  matchedRequestItemIds: Set<number>;
  organizationSlug: string;
}

export default function QuoteItemMatchSelector({
  quoteItem,
  availableRequestItems,
  matchedRequestItemIds,
  organizationSlug,
}: QuoteItemMatchSelectorProps) {
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Build options including "Sin asignar" option
  const options = [
    { id: null, label: "Sin asignar", description: "Quitar asignación" },
    ...availableRequestItems.map((item) => ({
      id: item.id,
      label: `Item #${item.item_number}`,
      description: item.description,
      isUnassigned: !matchedRequestItemIds.has(item.id),
    })),
  ];

  // Find current selection
  const currentSelection = options.find(
    (option) => option.id === quoteItem.request_item?.id,
  );

  const handleSelect = (selectedId: number | null) => {
    setProcessing(true);

    // Single unified API call - always PATCH the quote_item
    router.patch(
      organizationQuoteItemPath(organizationSlug, quoteItem.id),
      {
        quote_item: {
          request_item_id: selectedId,
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setOpen(false);
          setProcessing(false);
        },
        onError: (errors) => {
          console.error("Error updating item match:", errors);
          setProcessing(false);
        },
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between text-left h-auto min-h-8 px-3 py-2"
          disabled={processing}
        >
          {currentSelection?.id ? (
            <div className="flex flex-col items-start min-w-0 space-y-1">
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  ✓ {currentSelection.label}
                </span>
              </div>
              <span className="text-xs text-muted-foreground truncate max-w-full">
                {currentSelection.description}
              </span>
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">
              Seleccionar item...
            </span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar item..." className="h-9" />
          <CommandList>
            <CommandEmpty>No se encontraron items.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const contentClassName = option.isUnassigned
                  ? "flex flex-col items-start min-w-0 flex-1 rounded bg-amber-50 text-amber-900 border border-amber-200 px-2 py-2"
                  : "flex flex-col items-start min-w-0 flex-1 px-2 py-2";
                const labelIsTruncated =
                  option.label.length > TRUNCATE_THRESHOLD;
                const descriptionIsTruncated =
                  (option.description?.length ?? 0) > TRUNCATE_THRESHOLD;
                const shouldShowTooltip =
                  labelIsTruncated || descriptionIsTruncated;

                const itemContent = (
                  <div className={contentClassName}>
                    <span className="text-sm font-medium truncate w-full">
                      {option.label}
                    </span>
                    {option.description && (
                      <span className="text-xs text-muted-foreground mt-1 truncate w-full">
                        {option.description}
                      </span>
                    )}
                  </div>
                );

                return (
                  <CommandItem
                    key={option.id ?? "no-match"}
                    value={option.label}
                    onSelect={() => handleSelect(option.id)}
                    className="flex items-start py-2"
                  >
                    {shouldShowTooltip ? (
                      <Tooltip>
                        <TooltipTrigger asChild>{itemContent}</TooltipTrigger>
                        <TooltipContent
                          side="right"
                          className="max-w-xs text-foreground"
                        >
                          <div className="space-y-1">
                            <div className="text-sm font-medium">
                              {option.label}
                            </div>
                            {option.description && (
                              <div className="text-xs text-foreground/80">
                                {option.description}
                              </div>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      itemContent
                    )}
                    <Check
                      className={`ml-2 h-4 w-4 flex-shrink-0 ${
                        option.id === quoteItem.request_item?.id
                          ? "opacity-100"
                          : "opacity-0"
                      }`}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
